import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import NlimsDataTable from '../../shared/NlimsDataTable/NlimsDataTable';
import CreateSupplementaryDatasetModal from '../Modals/CreateSupplementaryDatasetModal';
import EditSupplementaryDatasetModal from '../Modals/EditSupplementaryDatasetModal';
import SupplementaryDatasetDetailsModal from '../Modals/SupplementaryDatasetDetailsModal';
import { supplementaryDatasetApi, dataSourcesApi, indicatorsApi } from '../../../api/supplementaryDatasetApi';
import { DATA_STATUSES, SOURCE_ORGANISATIONS } from '../data/enums';
import { formatNumber, humanize, orDash } from '../shared/format';
import SystemRowMenu, { standardRowItems, confirmAction } from '../shared/SystemRowMenu';
import { anchorRect } from '../../shared/useAnchoredMenu';

const PERIOD_OPTIONS = Array.from({ length: 7 }, (_, i) =>
  ['Q1', 'Q2', 'Q3', 'Q4'].map((q) => `${2020 + i}-${q}`)
).flat();

const DEFAULT_PER_PAGE = 15;

function parseError(error, fallback = 'Unable to load supplementary datasets.') {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
}

function SearchCreateRow({ query, setQuery, onSearch, onCreate, createLabel = 'Create New' }) {
  return (
    <div className="mb-4 flex items-center gap-3">
      <div className="relative flex-1">
        <svg
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => event.key === 'Enter' && onSearch()}
          placeholder="Search dataset, source, indicator, period, or status"
          className="w-full rounded-lg border border-gray-200 py-2.5 pl-9 pr-4 text-sm text-gray-700 placeholder-gray-400 focus:border-green-400 focus:outline-none focus:ring-1 focus:ring-green-400"
        />
      </div>
      <button
        type="button"
        onClick={onSearch}
        className="rounded-lg border border-green-200 bg-green-50 px-5 py-2.5 text-sm font-semibold text-green-700 hover:bg-green-100"
      >
        Search
      </button>
      <button
        type="button"
        onClick={onCreate}
        className="flex items-center gap-1.5 rounded-lg bg-green-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-green-800"
      >
        <span className="text-base font-bold leading-none">+</span> {createLabel}
      </button>
    </div>
  );
}

function ControlField({ label, children }) {
  return (
    <label className="flex flex-col gap-1.5 text-sm font-medium text-gray-700">
      <span>{label}</span>
      {children}
    </label>
  );
}

const SELECT_CLS = 'w-full appearance-none rounded-xl border border-gray-200 bg-white px-3 py-2.5 pr-9 text-sm text-gray-700 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500';
const INPUT_CLS = 'w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 placeholder:text-gray-400 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500';

function FilterSelect({ value, onChange, children }) {
  return (
    <div className="relative">
      <select value={value} onChange={(e) => onChange(e.target.value)} className={SELECT_CLS}>
        {children}
      </select>
      <svg
        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2.5}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  );
}

function BackendFilters({
  statusFilter,
  onStatusChange,
  sourceFilter,
  onSourceChange,
  periodFilter,
  onPeriodChange,
  indicatorFilter,
  onIndicatorChange,
  onClear,
  dataSources = [],
  indicatorOptions = [],
}) {
  const hasFilters =
    statusFilter !== 'All' ||
    Boolean(String(sourceFilter || '').trim()) ||
    Boolean(String(periodFilter || '').trim()) ||
    Boolean(String(indicatorFilter || '').trim());

  return (
    <div className="mb-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-gray-900">Backend filters</p>
          <p className="text-xs text-gray-500">
            These values are sent to <code>/api/v1/datasets</code> before the table loads.
          </p>
        </div>
        <button
          type="button"
          onClick={onClear}
          disabled={!hasFilters}
          className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Clear filters
        </button>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <ControlField label="Status">
          <FilterSelect value={statusFilter} onChange={onStatusChange}>
            <option value="All">All statuses</option>
            {DATA_STATUSES.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </FilterSelect>
        </ControlField>

        <ControlField label="Source Organisation">
          <FilterSelect value={sourceFilter} onChange={onSourceChange}>
            <option value="">All sources</option>
            {(dataSources.length > 0 ? dataSources : SOURCE_ORGANISATIONS).map((source) => (
              <option key={source.value} value={source.value}>
                {source.label}
              </option>
            ))}
          </FilterSelect>
        </ControlField>

        <ControlField label="Reference Period">
          <FilterSelect value={periodFilter} onChange={onPeriodChange}>
            <option value="">All periods</option>
            {PERIOD_OPTIONS.map((period) => (
              <option key={period} value={period}>
                {period}
              </option>
            ))}
          </FilterSelect>
        </ControlField>

        <ControlField label="Indicator Reference">
          {indicatorOptions.length > 0 ? (
            <FilterSelect value={indicatorFilter} onChange={onIndicatorChange}>
              <option value="">All indicators</option>
              {indicatorOptions.map((indicator) => (
                <option key={indicator.value} value={indicator.value}>
                  {indicator.label}
                </option>
              ))}
            </FilterSelect>
          ) : (
            <input
              type="text"
              value={indicatorFilter}
              onChange={(e) => onIndicatorChange(e.target.value)}
              placeholder="e.g. IMP-1"
              className={INPUT_CLS}
            />
          )}
        </ControlField>
      </div>
    </div>
  );
}

function ActiveFilters({ items }) {
  if (!items.length) return null;

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl border border-green-100 bg-green-50 px-4 py-3 text-sm text-green-800">
      <span className="font-semibold">Applied filters:</span>
      {items.map((item) => (
        <span
          key={item}
          className="rounded-full border border-green-200 bg-white px-3 py-1 text-xs font-medium text-green-700"
        >
          {item}
        </span>
      ))}
    </div>
  );
}

function statusBadgeHtml(status, type) {
  if (type !== 'display') return status ?? '';
  if (!status) return '-';

  const tones = {
    draft: 'background:#f3f4f6;color:#6b7280;border:1px solid #e5e7eb;',
    submitted: 'background:#eff6ff;color:#1d4ed8;border:1px solid #bfdbfe;',
    validated: 'background:#fff7ed;color:#c2410c;border:1px solid #fed7aa;',
    approved: 'background:#dcfce7;color:#15803d;border:1px solid #86efac;',
    rejected: 'background:#fee2e2;color:#b91c1c;border:1px solid #fecaca;',
  };

  const style = tones[String(status).toLowerCase()] || tones.draft;
  return `<span style="display:inline-flex;padding:2px 10px;border-radius:6px;font-size:11px;font-weight:600;${style}">${humanize(status)}</span>`;
}

function formatLocation(row) {
  if (!row) return '-';
  if (String(row.geographic_scope ?? '').toLowerCase() === 'national') return 'National';
  const parts = [row.state, row.lga].filter((value) => value !== null && value !== undefined && String(value).trim() !== '');
  return parts.length > 0 ? parts.join(' / ') : '-';
}

function datasetActionHtml() {
  return `<button type="button" class="nlims-action-btn" data-action="menu" style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:3px 12px;cursor:pointer;color:#15803d;font-weight:800;font-size:15px;letter-spacing:2px;line-height:1.4;">&#8943;</button>`;
}

const COLUMNS = [
  { title: 'Dataset Name', data: 'dataset_name', sortable: true, render: (d, t) => (t === 'display' ? orDash(d) : (d ?? '')) },
  { title: 'Source Organisation', data: 'source_organisation', sortable: true, render: (d, t) => (t === 'display' ? orDash(d) : (d ?? '')) },
  { title: 'Indicator Reference', data: 'indicator_reference', sortable: true, render: (d, t) => (t === 'display' ? orDash(d) : (d ?? '')) },
  { title: 'Reference Period', data: 'reference_period', sortable: true, render: (d, t) => (t === 'display' ? orDash(d) : (d ?? '')) },
  { title: 'Geographic Scope', data: 'geographic_scope', sortable: true, render: (d, t) => (t === 'display' ? humanize(d) : (d ?? '')) },
  { title: 'Location', data: null, sortable: false, render: (_d, t, row) => (t === 'display' ? formatLocation(row) : `${row?.state ?? ''} ${row?.lga ?? ''}`.trim()) },
  { title: 'Value Numeric', data: 'value_numeric', sortable: true, render: (d, t) => (t === 'display' ? formatNumber(d) : (d ?? '')) },
  { title: 'Data Status', data: 'data_status', sortable: true, render: statusBadgeHtml },
  { title: 'Action', data: null, sortable: false, render: () => datasetActionHtml() },
];

export default function SupplementaryDatasets() {
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [dataSources, setDataSources] = useState([]);
  const [indicatorOptions, setIndicatorOptions] = useState([]);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [sourceFilter, setSourceFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [indicatorFilter, setIndicatorFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [rowMenu, setRowMenu] = useState(null);

  const clientFilters = useMemo(() => {
    const params = {};
    if (statusFilter !== 'All') params.data_status = statusFilter;
    if (String(sourceFilter || '').trim()) params.source_organisation = String(sourceFilter).trim();
    if (String(periodFilter || '').trim()) params.reference_period = String(periodFilter).trim();
    if (String(indicatorFilter || '').trim()) params.indicator_reference = String(indicatorFilter).trim();
    return params;
  }, [indicatorFilter, periodFilter, sourceFilter, statusFilter]);

  const activeFilters = useMemo(() => {
    const items = [];
    if (statusFilter !== 'All') {
      const label = DATA_STATUSES.find((status) => status.value === statusFilter)?.label || humanize(statusFilter);
      items.push(`Status: ${label}`);
    }
    if (String(sourceFilter || '').trim()) items.push(`Source: ${String(sourceFilter).trim()}`);
    if (String(periodFilter || '').trim()) items.push(`Period: ${String(periodFilter).trim()}`);
    if (String(indicatorFilter || '').trim()) items.push(`Indicator: ${String(indicatorFilter).trim()}`);
    if (search) items.push(`Search: ${search}`);
    return items;
  }, [indicatorFilter, periodFilter, search, sourceFilter, statusFilter]);

  const loadData = useCallback(async () => {
    setError('');
    try {
      const result = await supplementaryDatasetApi.listAll({ per_page: 200 });
      setRows(result.items ?? []);
    } catch (err) {
      setError(parseError(err));
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    dataSourcesApi.list().then((items) => {
      if (items.length > 0) setDataSources(items);
    });
    indicatorsApi.list().then((items) => {
      if (items.length > 0) setIndicatorOptions(items);
    });
  }, []);

  const handleSearch = () => {
    setSearch(query.trim());
  };

  const handleRetry = () => {
    loadData();
  };

  const handleClearFilters = () => {
    setStatusFilter('All');
    setSourceFilter('');
    setPeriodFilter('');
    setIndicatorFilter('');
  };

  const handleCreate = async (payload) => {
    await supplementaryDatasetApi.create(payload);
    loadData();
  };

  // Edit modal save handler — branches on the modal's payload the same way
  // PopulationDataTable.handleEditSave does: a plain save only updates fields,
  // while submitAfterSave (the modal's explicit "Submit for Review" action)
  // additionally advances the record to "submitted" via a separate API call.
  const handleSave = async (payload) => {
    const { submitAfterSave, ...body } = payload;
    let updated = await supplementaryDatasetApi.update(body.uuid, body);
    if (submitAfterSave) {
      updated = await supplementaryDatasetApi.submit(body.uuid);
    }
    if (detailRow?.uuid === updated.uuid) {
      setDetailRow(updated);
    }
    loadData();
  };

  const handleDelete = async (row) => {
    if (!row?.uuid) return;
    const confirmed = await confirmAction({ title: 'Delete Record?', message: `Delete ${row.dataset_name || 'this dataset'}?`, confirmLabel: 'Delete', danger: true });
    if (!confirmed) return;

    try {
      await supplementaryDatasetApi.remove(row.uuid);
      if (detailRow?.uuid === row.uuid) setDetailRow(null);
      if (editRow?.uuid === row.uuid) setEditRow(null);
      loadData();
    } catch (err) {
      toast.error(parseError(err, 'Unable to delete dataset.'), { position: 'top-right' });
    }
  };

  const handleView = async (row) => {
    if (!row?.uuid) return;
    setDetailRow(row);

    try {
      const detail = await supplementaryDatasetApi.get(row.uuid);
      setDetailRow(detail);
    } catch (err) {
      toast.error(parseError(err, 'Unable to load dataset details.'), { position: 'top-right' });
    }
  };

  const handleRowAction = async (action, row, meta) => {
    if (action === 'menu') {
      setRowMenu({ row, rect: anchorRect(meta?.button) });
      return;
    }
    if (action === 'view') {
      await handleView(row);
      return;
    }
    if (action === 'edit') {
      setDetailRow(null);
      setEditRow(row);
      return;
    }
    if (action === 'delete') {
      await handleDelete(row);
    }
  };

  const handleWorkflowAction = async (action) => {
    if (!detailRow?.uuid) return;

    try {
      let updated;

      if (action === 'reject') {
        const rejectionNotes = window.prompt(
          'Enter rejection notes before rejecting this dataset.',
          detailRow.rejection_notes || ''
        );
        if (rejectionNotes === null) return;
        if (!String(rejectionNotes).trim()) {
          toast.error('Rejection notes are required.', { position: 'top-right' });
          return;
        }
        updated = await supplementaryDatasetApi.reject(detailRow.uuid, rejectionNotes);
      } else if (typeof supplementaryDatasetApi[action] === 'function') {
        updated = await supplementaryDatasetApi[action](detailRow.uuid);
      } else {
        throw new Error(`Unsupported workflow action: ${action}`);
      }

      setDetailRow(updated);
      loadData();

      const labelMap = {
        submit: 'submitted',
        validate: 'validated',
        approve: 'approved',
        reject: 'rejected',
      };
      toast.success(`Dataset ${labelMap[action] || action}.`, { position: 'top-right' });
    } catch (err) {
      toast.error(parseError(err, `Unable to ${action} dataset.`), { position: 'top-right' });
    }
  };

  return (
    <section>
      <SearchCreateRow
        query={query}
        setQuery={setQuery}
        onSearch={handleSearch}
        onCreate={() => setCreateOpen(true)}
      />

      <BackendFilters
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        sourceFilter={sourceFilter}
        onSourceChange={setSourceFilter}
        periodFilter={periodFilter}
        onPeriodChange={setPeriodFilter}
        indicatorFilter={indicatorFilter}
        onIndicatorChange={setIndicatorFilter}
        onClear={handleClearFilters}
        dataSources={dataSources}
        indicatorOptions={indicatorOptions}
      />

      <ActiveFilters items={activeFilters} />

      {error && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>{error}</span>
          <button
            type="button"
            onClick={handleRetry}
            className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100"
          >
            Retry
          </button>
        </div>
      )}

      <NlimsDataTable
        tableId="supplementary-datasets-table"
        columns={COLUMNS}
        data={rows}
        serverSide={false}
        filters={clientFilters}
        searchQuery={search}
        hideSearch
        pageLength={DEFAULT_PER_PAGE}
        enableRowSelection
        defaultSort={[0, 'asc']}
        onRowAction={handleRowAction}
      />

      {rowMenu && (
        <SystemRowMenu
          rect={rowMenu.rect}
          items={standardRowItems()}
          onClose={() => setRowMenu(null)}
          onPick={(key) => handleRowAction(key, rowMenu.row)}
        />
      )}

      <CreateSupplementaryDatasetModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
        dataSources={dataSources}
        indicatorOptions={indicatorOptions}
      />

      <EditSupplementaryDatasetModal
        isOpen={!!editRow}
        onClose={() => setEditRow(null)}
        onSave={handleSave}
        row={editRow}
        dataSources={dataSources}
        indicatorOptions={indicatorOptions}
      />

      <SupplementaryDatasetDetailsModal
        isOpen={!!detailRow}
        onClose={() => {
          setDetailRow(null);
        }}
        onEdit={(row) => {
          setDetailRow(null);
          setEditRow(row);
        }}
        onDelete={(row) => {
          setDetailRow(null);
          handleDelete(row);
        }}
        onWorkflowAction={handleWorkflowAction}
        row={detailRow}
      />
    </section>
  );
}
